import { DownloadOutlined, ReloadOutlined } from '@ant-design/icons';
import { Button, Image, Input, Table, TableProps, Tag } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useMemo } from 'react';
import { ProductSize } from '../../../../api-client';
import { AdminProductFilters } from '../../../hooks/useProduct';

type BVProductsTableProps = {
	products: ProductSize[];
	loading: boolean;
	onRefresh: () => void | Promise<void>;
	filters: AdminProductFilters;
	onFilterChange: (key: keyof AdminProductFilters, value: string) => void;
	enabledFilter: boolean | undefined;
	onEnabledFilterChange: (value: boolean | undefined) => void;
	loadingExport: boolean;
	onExport: () => void | Promise<void>;
	page: number;
	setPage: (page: number) => void;
	productsCount: number;
};

const BVProductsTable = ({
	products,
	loading,
	onRefresh,
	filters,
	onFilterChange,
	enabledFilter,
	onEnabledFilterChange,
	loadingExport,
	onExport,
	page,
	setPage,
	productsCount,
}: BVProductsTableProps) => {
	const columns: ColumnsType<ProductSize> = useMemo(
		() => [
			{
				title: 'Image',
				key: 'image',
				width: 90,
				render: (_, record) => {
					const product = record.product as ProductSize['product'] & {
						productAssets?: { url?: string | null }[];
					};
					const assets = product?.productAssets ?? product?.productAssests;
					const url = assets?.find((a) => a.url)?.url;

					return url ? (
						<Image
							src={url}
							width={50}
							height={50}
							style={{ objectFit: 'contain' }}
							preview
						/>
					) : (
						'-'
					);
				},
			},
			{
				title: 'SAP Code',
				key: 'sapCode',
				render: (_, record) => record.idSapCode ?? '-',
			},
			{
				title: 'Catalog Code',
				key: 'idCatCode',
				render: (_, record) => record.product?.idCatCode ?? '-',
			},
			{
				title: 'Aesthetic Motif',
				key: 'aestheticMotifCode',
				render: (_, record) => record.product?.aestheticMotifCode ?? '-',
			},
			{
				title: 'Aesthetic Line',
				key: 'aestheticLine',
				render: (_, record) => record.product?.aestheticLine ?? '-',
			},
			{
				title: 'Description',
				key: 'descriptionShortMarketing',
				render: (_, record) => record.product?.descriptionShortMarketing ?? '-',
			},
			{
				title: 'Enabled',
				key: 'enabled',
				width: 120,
				filters: [
					{ text: 'Enabled', value: true },
					{ text: 'Disabled', value: false },
				],
				filterMultiple: false,
				filteredValue: enabledFilter === undefined ? null : [enabledFilter],
				render: (_, record) =>
					record.product?.enabled ? (
						<Tag color="green">YES</Tag>
					) : (
						<Tag color="red">NO</Tag>
					),
			},
		],
		[enabledFilter],
	);

	const handleTableChange: TableProps<ProductSize>['onChange'] = (
		pagination,
		filters,
		_,
		extra,
	) => {
		setPage(pagination.current ?? 1);

		if (extra.action !== 'filter') {
			return;
		}

		const raw = (filters.enabled ?? null) as (string | boolean)[] | null;

		if (!raw || raw.length !== 1) {
			onEnabledFilterChange(undefined);
			return;
		}

		const v = raw[0];
		const parsed =
			v === true || v === 'true'
				? true
				: v === false || v === 'false'
					? false
					: undefined;

		onEnabledFilterChange(parsed);
	};

	return (
		<div className="admin-card" style={{ marginTop: 16 }}>
			<div
				style={{
					display: 'flex',
					justifyContent: 'space-between',
					alignItems: 'center',
				}}
			>
				<label className="admin-label">Products</label>

				<Button icon={<ReloadOutlined />} onClick={onRefresh} loading={loading}>
					Refresh
				</Button>
				<Button
					icon={<DownloadOutlined />}
					onClick={onExport}
					loading={loadingExport}
				>
					Download
				</Button>
			</div>

			<div
				style={{
					display: 'grid',
					gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
					gap: 12,
					marginTop: 12,
				}}
			>
				<Input
					placeholder="Filter SAP Code"
					value={filters.sapCode}
					onChange={(event) => onFilterChange('sapCode', event.target.value)}
					allowClear
				/>
				<Input
					placeholder="Filter Catalog Code"
					value={filters.catalogCode}
					onChange={(event) =>
						onFilterChange('catalogCode', event.target.value)
					}
					allowClear
				/>
				<Input
					placeholder="Filter Aesthetic Motif"
					value={filters.aestheticMotif}
					onChange={(event) =>
						onFilterChange('aestheticMotif', event.target.value)
					}
					allowClear
				/>
				<Input
					placeholder="Filter Aesthetic Line"
					value={filters.aestheticLine}
					onChange={(event) =>
						onFilterChange('aestheticLine', event.target.value)
					}
					allowClear
				/>
			</div>

			<Table<ProductSize>
				style={{ marginTop: 12 }}
				rowKey={(r) => String(r.id)}
				loading={loading}
				dataSource={products}
				columns={columns}
				pagination={{
					current: page,
					pageSize: 20,
					showSizeChanger: false,
					onChange: (p) => setPage(p),
					total: productsCount,
				}}
				onChange={handleTableChange}
			/>
		</div>
	);
};

export default BVProductsTable;
